import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { COUNTRY_OPTIONS } from '../../constants/profileOptions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const SettingsBilling = () => {
  const { userProfile, updateUserProfile, refreshUserProfile } = useAuth();
  const toast = useToast();
  const { t } = useTranslation('settings');
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    vatNumber: '',
    siret: '',
    phone: '',
    customerType: 'individual',
    addressLine1: '',
    addressLine2: '',
    postalCode: '',
    city: '',
    country: 'FR',
  });

  useEffect(() => {
    if (!userProfile) return;
    setFormData({
      firstName: userProfile.billing?.first_name || '',
      lastName: userProfile.billing?.last_name || '',
      companyName: userProfile.billing?.company_name || '',
      vatNumber: userProfile.billing?.vat_number || '',
      siret: userProfile.billing?.siret || '',
      phone: userProfile.billing?.phone || '',
      customerType: userProfile.billing?.customer_type || userProfile.legal?.user_type || 'individual',
      addressLine1: userProfile.billing?.address?.line1 || '',
      addressLine2: userProfile.billing?.address?.line2 || '',
      postalCode: userProfile.billing?.address?.postal_code || '',
      city: userProfile.billing?.address?.city || '',
      country: userProfile.billing?.address?.country || userProfile.legal?.country || 'FR',
    });
  }, [userProfile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateUserProfile({
        billing: {
          first_name: formData.firstName,
          last_name: formData.lastName,
          company_name: formData.companyName,
          vat_number: formData.vatNumber,
          siret: formData.siret,
          phone: formData.phone,
          customer_type: formData.customerType,
          address: {
            line1: formData.addressLine1,
            line2: formData.addressLine2,
            postal_code: formData.postalCode,
            city: formData.city,
            country: formData.country,
          },
        },
        legal: {
          user_type: formData.customerType,
        },
      });
      toast.success(t('billing.toastSaved'));
      await refreshUserProfile();
    } catch (error) {
      toast.error(error?.message || t('billing.toastError'));
    } finally {
      setSaving(false);
    }
  };

  const InputField = ({ id, label, value, onChange, placeholder, required = false, className = "" }) => (
    <div className={`space-y-2 ${className}`}>
      <label htmlFor={id} className="text-sm font-medium text-slate-700 dark:text-gray-300">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        id={id}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-slate-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white transition-all"
      />
    </div>
  );

  const customerTypes = [
    { value: 'individual', label: t('billing.individual') },
    { value: 'business', label: t('billing.business') },
  ];

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center justify-between border-b border-slate-200 dark:border-gray-800 pb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">{t('billing.title')}</h2>
          <p className="text-sm text-slate-500 dark:text-gray-400 mt-1">
            {t('billing.subtitle')}
          </p>
        </div>
        <div className="hidden sm:block">
            <div className="h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
                <FontAwesomeIcon icon="file-invoice-dollar" className="text-xl" />
            </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Type de client */}
        <div className="bg-slate-50 dark:bg-gray-900/30 p-4 rounded-xl border border-slate-200 dark:border-gray-800">
            <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-3">{t('billing.accountType')}</label>
            <div className="flex gap-3">
            {customerTypes.map((option) => (
                <button
                type="button"
                key={option.value}
                onClick={() => setFormData({ ...formData, customerType: option.value })}
                className={`flex-1 sm:flex-none px-6 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                    formData.customerType === option.value
                    ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/20'
                    : 'bg-white dark:bg-gray-800 text-slate-600 dark:text-gray-300 border border-slate-200 dark:border-gray-700 hover:bg-slate-50 dark:hover:bg-gray-700'
                }`}
                >
                <FontAwesomeIcon icon={option.value === 'business' ? 'building' : 'user'} />
                {option.label}
                </button>
            ))}
            </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
            <InputField
                id="firstName"
                label={t('billing.firstName')}
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
            />
            <InputField
                id="lastName"
                label={t('billing.lastName')}
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
            />
        </div>

        {formData.customerType === 'business' && (
            <div className="space-y-6 p-6 bg-slate-50 dark:bg-gray-900/30 rounded-xl border border-slate-200 dark:border-gray-800 animate-in fade-in zoom-in-95 duration-300">
                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 dark:text-gray-400 mb-4">{t('billing.companyInfo')}</h3>
                <InputField
                    id="companyName"
                    label={t('billing.companyName')}
                    value={formData.companyName}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                    required
                />
                <div className="grid gap-6 md:grid-cols-2">
                    <InputField
                        id="vatNumber"
                        label={t('billing.vatNumber')}
                        value={formData.vatNumber}
                        onChange={(e) => setFormData({ ...formData, vatNumber: e.target.value })}
                        placeholder="FRXX999999999"
                    />
                    <InputField
                        id="siret"
                        label={t('billing.siret')}
                        value={formData.siret}
                        onChange={(e) => setFormData({ ...formData, siret: e.target.value })}
                    />
                </div>
            </div>
        )}

        <div className="space-y-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white border-b border-slate-200 dark:border-gray-800 pb-2">{t('billing.contact')}</h3>
            
            <InputField
                id="phone"
                label={t('billing.phone')}
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+33 6 12 34 56 78"
            />

            <InputField
                id="addressLine1"
                label={t('billing.address')}
                value={formData.addressLine1}
                onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
                required
            />
            
            <InputField
                id="addressLine2"
                label={t('billing.addressComplement')}
                value={formData.addressLine2}
                onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
                placeholder={t('billing.addressPlaceholder')}
            />

            <div className="grid gap-6 md:grid-cols-3">
                <InputField
                    id="postalCode"
                    label={t('billing.postalCode')}
                    value={formData.postalCode}
                    onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                    required
                />
                <InputField
                    id="city"
                    label={t('billing.city')}
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    required
                    className="md:col-span-2"
                />
            </div>

            <div className="space-y-2">
                <label htmlFor="billingCountry" className="text-sm font-medium text-slate-700 dark:text-gray-300">
                    {t('billing.country')}
                </label>
                <div className="relative">
                    <select
                        id="billingCountry"
                        value={formData.country}
                        onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                        className="w-full appearance-none rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-slate-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white transition-all"
                    >
                        {COUNTRY_OPTIONS.map((option) => (
                        <option key={option.code} value={option.code}>
                            {option.label}
                        </option>
                        ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-slate-500">
                        <FontAwesomeIcon icon="chevron-down" className="text-xs" />
                    </div>
                </div>
            </div>
        </div>

        <div className="flex justify-end pt-6 border-t border-slate-200 dark:border-gray-800">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-8 py-3 font-semibold text-white transition-all hover:bg-indigo-500 hover:shadow-lg hover:shadow-indigo-500/20 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? (
                <>
                    <FontAwesomeIcon icon="circle-notch" spin />
                    <span>{t('billing.saving')}</span>
                </>
            ) : (
                <>
                    <FontAwesomeIcon icon="save" />
                    <span>{t('billing.save')}</span>
                </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SettingsBilling;
